import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Calendar, MapPin, ExternalLink, Users, ChevronLeft, ChevronRight } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

interface Event {
  id: string
  title: string
  description: string
  date: string
  location: string
  image: string
  imageAlt: string
  type: 'event' | 'seminar' | 'sponsorship'
  url?: string
  fullDescription?: string
  attendees?: string
  organizer?: string
}

interface EventDetailProps {
  event: Event | null
  isOpen: boolean
  onClose: () => void
}

const EventDetail: React.FC<EventDetailProps> = ({ event, isOpen, onClose }) => {
  const { t } = useLanguage()

  // Carousel images for event detail
  const carouselImages = [
    '/events/tec-2025/img-1.jpeg',
    '/events/tec-2025/img-2.jpeg',
    '/events/tec-2025/img-3.jpeg',
    '/events/tec-2025/img-4.jpeg',
    '/events/tec-2025/img-5.jpeg'
  ]

  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  // Reset carousel when modal opens or event changes
  useEffect(() => {
    if (isOpen && event) {
      setCurrentImageIndex(0)
    }
  }, [isOpen, event?.id])

  // Auto-advance carousel
  useEffect(() => {
    if (!isOpen) return

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length)
    }, 5000) // Change image every 5 seconds

    return () => clearInterval(interval)
  }, [isOpen, carouselImages.length])

  const goToPrevious = () => {
    setCurrentImageIndex((prev) => (prev - 1 + carouselImages.length) % carouselImages.length)
  }

  const goToNext = () => {
    setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length)
  }

  if (!event) return null

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'event':
        return 'bg-blue-500'
      case 'seminar':
        return 'bg-purple-500'
      case 'sponsorship':
        return 'bg-green-500'
      default:
        return 'bg-gray-500'
    }
  }

  const getTypeLabel = (type: string) => {
    return t(`events.types.${type}`)
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative z-[60]"
            >
              {/* Close Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onClose()
                }}
                className="absolute top-4 right-4 z-[70] p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
                aria-label="Close"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>

              {/* Image Carousel */}
              <div className="relative h-64 w-full overflow-hidden group">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentImageIndex}
                    src={carouselImages[currentImageIndex]}
                    alt={event.imageAlt || event.title}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5 }}
                    className="w-full h-full object-cover pointer-events-none select-none"
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement
                      target.src = '/events/tec-2025/img-1.jpeg'
                    }}
                  />
                </AnimatePresence>

                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />

                {/* Navigation Arrows */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    goToPrevious()
                  }}
                  className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 active:bg-black/80 rounded-full p-2 shadow-lg transition-all z-[70] opacity-100 sm:opacity-0 sm:group-hover:opacity-100 touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-5 h-5 text-white" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    goToNext()
                  }}
                  className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 active:bg-black/80 rounded-full p-2 shadow-lg transition-all z-[70] opacity-100 sm:opacity-0 sm:group-hover:opacity-100 touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-5 h-5 text-white" />
                </button>

                {/* Title and Badge */}
                <div className="absolute bottom-4 left-4 right-4 z-[65] pointer-events-none">
                  <div className={`inline-block px-3 py-1 rounded-full text-white text-sm font-semibold mb-2 ${getTypeColor(event.type)}`}>
                    {getTypeLabel(event.type)}
                  </div>
                  <h2 className="text-3xl font-bold text-white">{event.title}</h2>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                {/* Event Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {event.date && (
                    <div className="flex items-center text-gray-700">
                      <Calendar className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0" />
                      <span className="font-medium">{event.date}</span>
                    </div>
                  )}
                  {event.location && (
                    <div className="flex items-center text-gray-700">
                      <MapPin className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0" />
                      <span className="font-medium">{event.location}</span>
                    </div>
                  )}
                  {event.attendees && (
                    <div className="flex items-center text-gray-700">
                      <Users className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0" />
                      <span className="font-medium">{event.attendees}</span>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t('events.details.about')}</h3>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {event.fullDescription || event.description}
                  </p>
                </div>

                {/* Organizer */}
                {event.organizer && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">{t('events.details.organizer')}</p>
                    <p className="font-medium text-gray-900">{event.organizer}</p>
                  </div>
                )}

                {/* Action Button */}
                {event.url && (
                  <div className="flex justify-end">
                    <a
                      href={event.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                    >
                      {t('events.viewPost')}
                      <ExternalLink className="w-5 h-5 ml-2" />
                    </a>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default EventDetail

